import React,{useState} from 'react';
import Icon from '../Text/Icon';
import Text from '../Text/Text';
import classNames from 'classnames';

// Tool name to display name mapping
const TOOL_DISPLAY_NAMES={
    'analyze_flowchart': 'Analyzing flowchart',
    'add_nodes': 'Adding nodes',
    'add_edges': 'Connecting nodes',
    'modify_node': 'Modifying node',
    'delete_elements': 'Deleting elements',
    'rearrange_layout': 'Rearranging layout',
    'get_suggestions': 'Getting suggestions',
    'think': 'Reasoning',
    'respond_to_user': 'Preparing response'
};

// Tool name to icon mapping
const TOOL_ICONS={
    'analyze_flowchart': 'search',
    'add_nodes': 'plus-circle',
    'add_edges': 'link',
    'modify_node': 'edit',
    'delete_elements': 'trash-2',
    'rearrange_layout': 'layout',
    'get_suggestions': 'lightbulb',
    'think': 'brain',
    'respond_to_user': 'message-circle'
};

/**
 * ToolCallDisplay - Shows a collapsible tool call
 * Simplified UI: Monochrome, results hidden by default, only reasoning shown
 */
export default function ToolCallDisplay({toolCall,isExpanded: initialExpanded=false}) {
    // Only 'think' tools are expanded by default if specified
    const [isExpanded,setIsExpanded]=useState(initialExpanded);

    const {name,args,result,status='success'}=toolCall;
    const displayName=TOOL_DISPLAY_NAMES[name]||name;
    const icon=TOOL_ICONS[name]||'tool';

    const isThinking=name==='think';

    // Format arguments summary for non-think tools
    const formatArgs=(name,args) => {
        if(!args) return null;
        try {
            const parsed=typeof args==='string'? JSON.parse(args):args;
            switch(name) {
                case 'add_nodes': {
                    const nodes=parsed.nodes||parsed;
                    return Array.isArray(nodes)? `${nodes.length} node${nodes.length!==1? 's':''}`:null;
                }
                case 'add_edges': {
                    const edges=parsed.edges||parsed;
                    return Array.isArray(edges)? `${edges.length} edge${edges.length!==1? 's':''}`:null;
                }
                case 'modify_node':
                    return parsed.nodeId? `Node: ${parsed.nodeId}`:(parsed.id? `Node: ${parsed.id}`:null);
                case 'delete_elements': {
                    const ids=parsed.nodeIds||parsed.ids||[];
                    return ids.length? `${ids.length} element${ids.length!==1? 's':''}`:null;
                }
                case 'rearrange_layout':
                    return parsed.algorithm||parsed.direction||null;
                case 'think':
                    return parsed.reasoning||parsed.analysisPrompt||null;
                default:
                    return null;
            }
        } catch {
            return typeof args==='string'? args:null;
        }
    };

    // Format result for display
    const formatResult=(result) => {
        if(!result) return null;
        if(typeof result==='string') return result;
        if(result.error) return `Error: ${result.error}`;
        if(result.message) return result.message;
        if(result.reasoning) return result.reasoning;
        if(result.success!==undefined) return result.success? 'Success':'Failed';
        return JSON.stringify(result,null,2);
    };

    const argsSummary=formatArgs(name,args);
    const resultText=formatResult(result);
    const hasDetails=!!(argsSummary||resultText);

    return (
        <div className="mb-1 last:mb-0">
            {/* Header */}
            <button
                onClick={() => hasDetails&&setIsExpanded(!isExpanded)}
                className={classNames(
                    'flex items-center gap-2 w-full px-2 py-1.5 rounded transition-colors',
                    'hover:bg-white/10',
                    hasDetails? 'cursor-pointer':'cursor-default'
                )}
            >
                {/* Status/Icon - Monochrome to avoid color clash */}
                <div className={classNames('flex items-center justify-center w-5 h-5 rounded',
                    status==='running'? 'animate-pulse text-white/80':'text-white/60'
                )}>
                    {status==='running'? (
                        <Icon icon="loader" className="text-xs animate-spin" />
                    ):(
                        <Icon icon={icon} className="text-xs" />
                    )}
                </div>

                {/* Tool name */}
                <Text className="text-xs text-white/70 flex-1 text-left font-medium">
                    {displayName}
                </Text>

                {/* Expand indicator - only for thinking steps */}
                {hasDetails&&(
                    <Icon
                        icon={isExpanded? 'chevron-up':'chevron-down'}
                        className="text-[10px] text-white/30"
                    />
                )}
            </button>

            {/* Expanded content */}
            {isExpanded&&hasDetails&&(
                <div className="mt-1 ml-8 pl-2 border-l border-white/10">
                    {argsSummary&&(
                        <div className="mb-1">
                            <Text className="text-[10px] text-white/40 uppercase tracking-wide">{isThinking? 'Reasoning':'Input'}</Text>
                            <Text className="text-xs text-white/60 whitespace-pre-wrap leading-relaxed">
                                {argsSummary}
                            </Text>
                        </div>
                    )}
                    {resultText&&!isThinking&&(
                        <div className="mb-1">
                            <Text className="text-[10px] text-white/40 uppercase tracking-wide">Result</Text>
                            <Text className="text-xs text-white/60 whitespace-pre-wrap leading-relaxed">
                                {resultText}
                            </Text>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

/**
 * ToolCallList - Shows a list of tool calls
 */
export function ToolCallList({toolCalls=[],isProcessing=false}) {
    if(toolCalls.length===0&&!isProcessing) return null;

    return (
        <div className="space-y-0.5 my-1">
            {toolCalls.map((toolCall,index) => (
                <ToolCallDisplay
                    key={toolCall.id||index}
                    toolCall={toolCall}
                    isExpanded={toolCall.name==='think'} // Auto-expand thinking
                />
            ))}

            {/* Processing indicator (if strictly needed, but tool calls usually show running status) */}
            {isProcessing&&(
                <div className="flex items-center gap-2 px-2 py-1.5 rounded opacity-50">
                    <div className="w-5 h-5 flex items-center justify-center">
                        <Icon icon="loader" className="text-xs text-white animate-spin" />
                    </div>
                    <Text className="text-xs text-white/50">Processing...</Text>
                </div>
            )}
        </div>
    );
}
